<template>
  <div class="dable-multiselect" ref="root">
    <button type="button" class="dable-filter-input dable-multiselect__trigger" :title="triggerTitle" @mousedown.stop.prevent @click.stop="toggle">
      <span class="dable-multiselect__label">{{ triggerLabel }}</span>
      <i class="fa fa-caret-down"></i>
    </button>
    <Teleport to="body">
      <div v-if="open" class="dable-multiselect__menu" :style="menuStyle" @mousedown.stop.prevent>
        <input
          ref="searchInput"
          v-model="query"
          type="text"
          class="dable-multiselect__search"
          placeholder="Filtro..."
          @keydown.esc.stop.prevent="close"
          @mousedown.stop
        />
        <div class="dable-multiselect__list">
          <button
            v-for="opt in filteredOptions"
            :key="opt.value"
            type="button"
            class="dable-multiselect__option"
            :class="{ 'dable-multiselect__option--checked': isChecked(opt.value) }"
            @mousedown.stop.prevent
            @click.stop.prevent="toggleValue(opt.value, !isChecked(opt.value))"
          >
            <span class="dable-multiselect__check" aria-hidden="true">
              <i v-if="isChecked(opt.value)" class="fa fa-check"></i>
            </span>
            <span class="dable-multiselect__text">{{ opt.label }}</span>
          </button>
          <div v-if="!filteredOptions.length" class="dable-multiselect__empty">Nuk u gjet asnjë rezultat</div>
        </div>
        <div class="dable-multiselect__footer">
          <button type="button" class="dable-multiselect__clear" :disabled="!filteredOptions.length" @mousedown.stop.prevent @click.stop.prevent="selectFiltered">Zgjidh të gjitha</button>
          <button type="button" class="dable-multiselect__clear" :disabled="!selected.length" @mousedown.stop.prevent @click.stop.prevent="clear">Pastro</button>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  props: {
    options: { type: Array, default: () => [] },
    modelValue: { default: () => [] },
  },
  emits: ["update:modelValue"],
  data() {
    return {
      open: false,
      query: "",
      menuStyle: {
        position: "fixed",
        top: "-9999px",
        left: "0px",
        width: "260px",
        zIndex: 5000,
      },
    };
  },
  computed: {
    selected() {
      const value = this.modelValue;
      if (Array.isArray(value)) return value.map((v) => String(v)).filter(Boolean);
      if (value == null || value === "") return [];
      return [String(value)];
    },
    filteredOptions() {
      const q = this.query.trim().toLowerCase();
      const opts = this.options || [];
      if (!q) return opts;
      return opts.filter((opt) => String(opt.label || opt.value || "").toLowerCase().includes(q));
    },
    triggerLabel() {
      if (!this.selected.length) return "Të gjitha";
      if (this.selected.length === 1) {
        const opt = (this.options || []).find((o) => String(o.value) === this.selected[0]);
        return opt?.label || this.selected[0];
      }
      return `${this.selected.length} zgjedhur`;
    },
    triggerTitle() {
      return this.selected.length ? this.selected.join(", ") : "Të gjitha";
    },
  },
  watch: {
    open(val) {
      if (val) {
        this.$nextTick(() => {
          this.positionMenu();
          this.$refs.searchInput?.focus?.({ preventScroll: true });
        });
        document.addEventListener("mousedown", this.onDocMouseDown, true);
        window.addEventListener("resize", this.positionMenu);
      } else {
        this.detach();
        this.query = "";
      }
    },
  },
  beforeUnmount() {
    this.detach();
  },
  methods: {
    detach() {
      document.removeEventListener("mousedown", this.onDocMouseDown, true);
      window.removeEventListener("resize", this.positionMenu);
    },
    toggle() {
      this.open = !this.open;
    },
    close() {
      this.open = false;
    },
    onDocMouseDown(event) {
      const root = this.$refs.root;
      const menu = event.target?.closest?.(".dable-multiselect__menu");
      if (root?.contains(event.target) || menu) return;
      this.close();
    },
    positionMenu() {
      const el = this.$refs.root;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const width = Math.max(rect.width, 260);
      let left = rect.left;
      if (left + width > window.innerWidth - 8) left = Math.max(8, window.innerWidth - width - 8);
      let top = rect.bottom + 4;
      const maxHeight = 320;
      if (top + maxHeight > window.innerHeight && rect.top > maxHeight) {
        top = rect.top - maxHeight - 4;
      }
      this.menuStyle = {
        position: "fixed",
        top: `${Math.max(8, top)}px`,
        left: `${left}px`,
        width: `${width}px`,
        zIndex: 5000,
      };
    },
    isChecked(value) {
      return this.selected.includes(String(value));
    },
    toggleValue(value, checked) {
      const next = [...this.selected];
      const key = String(value);
      const idx = next.indexOf(key);
      if (checked && idx === -1) next.push(key);
      if (!checked && idx !== -1) next.splice(idx, 1);
      this.$emit("update:modelValue", next);
    },
    selectFiltered() {
      const next = [...this.selected];
      this.filteredOptions.forEach((opt) => {
        const key = String(opt.value);
        if (!next.includes(key)) next.push(key);
      });
      this.$emit("update:modelValue", next);
    },
    clear() {
      this.$emit("update:modelValue", []);
    },
  },
};
</script>

<style scoped>
.dable-multiselect {
  width: 100%;
  min-width: 0;
}

.dable-multiselect__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.25rem;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.2rem 0.35rem;
  font-size: 0.75rem;
  background: #fff;
  color: #334155;
}

.dable-multiselect__label {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>

<style>
.dable-multiselect__menu {
  position: fixed;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #dbeafe;
  border-radius: 6px;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  padding: 0.4rem;
  overflow-anchor: none;
}

.dable-multiselect__search {
  display: block;
  box-sizing: border-box;
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 4px;
  padding: 0.25rem 0.4rem;
  font-size: 0.8rem;
  margin: 0 0 0.3rem 0;
}

.dable-multiselect__list {
  max-height: 220px;
  overflow-y: auto;
}

.dable-multiselect__option {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 0.5rem;
  width: 100%;
  margin: 0;
  padding: 0.28rem 0.3rem;
  border: 0;
  background: transparent;
  font-size: 0.8rem;
  line-height: 1.3;
  color: #334155;
  text-align: left;
  cursor: pointer;
}

.dable-multiselect__option:hover,
.dable-multiselect__option--checked {
  background: #eff6ff;
}

.dable-multiselect__check {
  flex: 0 0 16px;
  width: 16px;
  height: 16px;
  margin-top: 1px;
  border: 1px solid #94a3b8;
  border-radius: 3px;
  background: #fff;
  color: #2563eb;
  font-size: 10px;
  line-height: 14px;
  text-align: center;
}

.dable-multiselect__option--checked .dable-multiselect__check {
  border-color: #2563eb;
  background: #dbeafe;
}

.dable-multiselect__text {
  flex: 1 1 auto;
  min-width: 0;
  text-align: left;
  white-space: normal;
  word-break: break-word;
}

.dable-multiselect__empty {
  padding: 0.45rem 0.25rem;
  font-size: 0.8rem;
  color: #64748b;
}

.dable-multiselect__footer {
  display: flex;
  justify-content: space-between;
  gap: 0.5rem;
  padding-top: 0.3rem;
  border-top: 1px solid #e2e8f0;
  margin-top: 0.3rem;
}

.dable-multiselect__clear {
  border: none;
  background: none;
  color: #2563eb;
  font-size: 0.75rem;
  cursor: pointer;
  padding: 0;
}

.dable-multiselect__clear:disabled {
  color: #94a3b8;
  cursor: default;
}
</style>
